import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Text, TouchableOpacity, View } from 'react-native';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { SoftPressable } from '../../shared/ui/SoftPressable';
import { showSuccess } from '../../shared/ui/appToast';
import { ModalShell } from '../../shared/modals/ModalShell';
import { listKitchenPrintJobs, type KitchenPrintJob } from '../../printing/infra/kitchenPrintJobsRepo';
import { retryKitchenPrintJob } from '../../printing/application/printComandaDocuments';
import { kitchenQueueStyles as styles } from './KitchenQueueScreen.styles';

const JOB_STATUS_LABELS: Record<KitchenPrintJob['status'], string> = {
  queued: 'En cola',
  retrying: 'Reintentando',
  failed: 'Falló',
  cancelled: 'Cancelada',
};

function jobMetaLine(job: KitchenPrintJob): string {
  const parts: string[] = [JOB_STATUS_LABELS[job.status]];
  if (job.stationName) parts.push(job.stationName);
  if (job.printerName) parts.push(`Impresora: ${job.printerName}`);
  if (job.attempts > 0) parts.push(`${job.attempts} intento${job.attempts === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

export type KitchenPrintJobsPanelProps = {
  visible: boolean;
  onClose: () => void;
  branchId: string | null;
  autoPrint?: boolean;
  onToggleAutoPrint?: () => void;
};

export function KitchenPrintJobsPanel({
  visible,
  onClose,
  branchId,
  autoPrint,
  onToggleAutoPrint,
}: KitchenPrintJobsPanelProps) {
  const [jobs, setJobs] = useState<KitchenPrintJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const loadJobs = useCallback(async () => {
    if (!branchId) {
      setJobs([]);
      setLoading(false);
      return;
    }
    try {
      setJobs(await listKitchenPrintJobs(branchId));
    } catch (e) {
      presentUnexpectedFailure({
        module: 'hospitality',
        op: 'impresiones',
        error: e,
        title: 'Impresiones',
        userMessage: 'No se pudieron cargar',
      });
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  // Recarga al abrir el modal: la cola de impresión tiene que estar fresca.
  useEffect(() => {
    if (visible) {
      setLoading(true);
      void loadJobs();
    }
  }, [visible, loadJobs]);

  const handleRetry = async (job: KitchenPrintJob) => {
    if (!branchId || retryingId) return;
    setRetryingId(job.id);
    try {
      await retryKitchenPrintJob({ branchId, job });
      showSuccess('Impresiones', 'Comanda reimpresa');
    } catch (e) {
      presentUnexpectedFailure({
        module: 'hospitality',
        op: 'impresiones',
        error: e,
        title: 'Impresiones',
        userMessage: 'No se pudo reimprimir',
      });
    } finally {
      setRetryingId(null);
      void loadJobs();
    }
  };

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      title="Impresora y comandas térmicas"
      kicker="Cocina"
      widthVariant="standard"
      closeAccessibilityLabel="Cerrar impresora"
    >
      <View style={styles.adminPanel} testID="hospitality.cocina.print-jobs-panel">
        {onToggleAutoPrint ? (
          <View style={styles.settingRowCard}>
            <View style={styles.settingInfoCol}>
              <Text style={styles.settingInfoTitle}>Auto-imprimir comandas nuevas</Text>
              <Text style={styles.settingInfoSub}>
                Imprime sola en la comandera térmica apenas entra
              </Text>
            </View>
            <TouchableOpacity
              onPress={onToggleAutoPrint}
              activeOpacity={0.75}
              style={[styles.togglePillBtn, autoPrint && styles.togglePillBtnOn]}
              accessibilityRole="button"
              accessibilityLabel={autoPrint ? 'Auto-imprimir: prendido' : 'Auto-imprimir: apagado'}
              testID="hospitality.cocina.auto-print"
            >
              <Text style={[styles.togglePillText, autoPrint && styles.togglePillTextOn]}>
                {autoPrint ? 'Prendido' : 'Apagado'}
              </Text>
            </TouchableOpacity>
          </View>
        ) : null}

        <View style={styles.settingRowCard}>
          <View style={styles.settingInfoCol}>
            <Text style={styles.settingInfoTitle}>Cola de impresión térmica</Text>
            <Text style={styles.settingInfoSub}>
              {loading
                ? 'Verificando cola de impresión…'
                : jobs.length === 0
                  ? 'Sin comandas pendientes de impresión'
                  : `${jobs.length} comanda${jobs.length === 1 ? '' : 's'} pendiente${jobs.length === 1 ? '' : 's'} de impresión`}
            </Text>
          </View>
          <View style={styles.livePill}>
            <View style={[styles.liveDot, jobs.length > 0 && styles.liveDotOffline]} />
            <Text style={styles.livePillText}>
              {jobs.length === 0 ? 'Al día' : `${jobs.length} en cola`}
            </Text>
          </View>
        </View>

        {loading ? (
          <ActivityIndicator color={ticketColors.gold} style={{ marginVertical: spacing.sm }} />
        ) : jobs.length > 0 ? (
          jobs.map((job) => (
            <View key={job.id} style={styles.stationCard} testID={`hospitality.cocina.print-job.${job.id}`}>
              <Text style={styles.stationNameText}>{JOB_STATUS_LABELS[job.status]}</Text>
              <Text style={styles.stationMeta}>{jobMetaLine(job)}</Text>
              {job.lastError ? <Text style={styles.stationMeta}>{job.lastError}</Text> : null}
              {job.status === 'cancelled' ? null : (
                <View style={styles.stationActionsRow}>
                  <SoftPressable
                    onPress={() => void handleRetry(job)}
                    disabled={retryingId != null}
                    style={[
                      styles.stationActionBtn,
                      styles.stationSaveBtn,
                      retryingId != null && { opacity: 0.45 },
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel="Reintentar impresión"
                    testID={`hospitality.cocina.print-job-retry.${job.id}`}
                  >
                    <Text style={styles.stationActionLabelPrimary}>
                      {retryingId === job.id ? 'Imprimiendo…' : 'Reintentar'}
                    </Text>
                  </SoftPressable>
                </View>
              )}
            </View>
          ))
        ) : null}
      </View>
    </ModalShell>
  );
}
